/**
 * Quote version export — browser side of the shared export renderers
 * (@iomi/shared quoteExport): clipboard writes and the .xlsx download.
 *
 * Clipboard writes carry two flavours at once: tab-separated text (pastes as
 * cells into Excel / Google Sheets / Numbers, and as aligned text anywhere
 * else) and an HTML table (pastes as a real table into Word, Google Docs,
 * Gmail and proposal tools' rich-text editors). Browsers without ClipboardItem
 * get the tab-separated text only.
 */

import {
  buildXlsxEntries,
  exportSectionsToHtml,
  exportSectionsToTsv,
  xlsxFileName,
  XLSX_MIME_TYPE,
  type ExportSection,
  type QuoteVersionExportPacket,
} from '@iomi/shared';

// ── Clipboard ───────────────────────────────────────────────────────────────

/**
 * Copies the sections as tab-separated text plus an HTML table. Must be called
 * synchronously from a user gesture (Safari refuses clipboard writes after an
 * await), which is why the packet is prefetched rather than loaded on tap.
 */
export async function copySections(packet: QuoteVersionExportPacket, sections: ExportSection[]): Promise<void> {
  const text = exportSectionsToTsv(packet, sections);
  const html = exportSectionsToHtml(packet, sections);
  const clip = navigator.clipboard;
  if (clip && typeof ClipboardItem !== 'undefined' && typeof clip.write === 'function') {
    try {
      await clip.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      return;
    } catch {
      // Fall through to plain text (e.g. Firefox without ClipboardItem write support).
    }
  }
  if (clip && typeof clip.writeText === 'function') {
    await clip.writeText(text);
    return;
  }
  // Last resort for very old browsers: a hidden textarea + execCommand.
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  if (!ok) throw new Error('Clipboard is not available in this browser');
}

// ── Spreadsheet (.xlsx) ─────────────────────────────────────────────────────
// The workbook entries come from @iomi/shared; jszip (already shipped by the
// app) zips them. Loaded on demand so it stays out of the shell bundle.

export async function buildXlsx(packet: QuoteVersionExportPacket): Promise<Blob> {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  for (const entry of buildXlsxEntries(packet)) zip.file(entry.path, entry.content);
  return zip.generateAsync({ type: 'blob', mimeType: XLSX_MIME_TYPE });
}

export async function downloadXlsx(packet: QuoteVersionExportPacket): Promise<void> {
  const blob = await buildXlsx(packet);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = xlsxFileName(packet);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
